import dayjs, { ManipulateType } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTranslation } from "react-i18next";
import { TimeFrame } from "~/types";

dayjs.extend(customParseFormat);

// Parse formats
const dayP = "DD-MM-YYYY";
const timeP = "HH:mm";
const dayTimeP = `${dayP} ${timeP}`;

// Display formats
const timeF = "h:mm\u00A0A";
const dayF = "Do";
const monthShortF = "MMM";
const monthLongF = "MMMM";
const yearF = "YYYY";

const dayTimeF = `${dayF}, ${timeF}`;
const monthDayTimeF = `${monthShortF} ${dayTimeF}`;
const monthDayYearTimeF = `${monthShortF} ${dayF}, ${yearF}; ${timeF}`;

export const firstOfCurrentMonth = () => {
  return dayjs().startOf("month").format(`${monthLongF} ${dayF}, ${yearF}`);
};

export const parseDayTime = (dayTime: string): dayjs.Dayjs => {
  return dayjs(dayTime, dayTimeP);
};

export const parseDate = (day: string, time: string): dayjs.Dayjs => {
  return dayjs(`${day} ${time}`, dayTimeP);
};

export const parseTime = (time: string): dayjs.Dayjs => {
  return dayjs(time, timeP);
};

export const timeIsInRange = (
  timeFrame: TimeFrame,
  time: dayjs.Dayjs
): boolean => {
  return timeFrame.start.isBefore(time) && timeFrame.end.isAfter(time);
};

export const simpleDateFormat = (date: dayjs.Dayjs): string => {
  return dateFormat(date, "D MMM YYYY; h:mm a");
};

export const simpleDayFormat = (date: dayjs.Dayjs): string => {
  return dateFormat(date, "D MMM YYYY");
};

export const dateFormat = (date: dayjs.Dayjs, format: string): string => {
  return date.format(format);
};

export const timeFrameTimeFormat = (timeFrame: TimeFrame): string =>
  timeFrameFormat(timeFrame, timeF, true);

const timeFrameFormats = (
  timeFrame: TimeFrame,
  formatStart: string,
  formatEnd: string,
  nonBreak?: boolean
): string => {
  if (nonBreak)
    return `${timeFrame.start.format(
      formatStart
    )}\u00A0\u2015\u00A0${timeFrame.end.format(formatEnd)}`;
  return `${timeFrame.start.format(formatStart)} \u2015 ${timeFrame.end.format(
    formatEnd
  )}`;
};

const timeFrameFormat = (
  timeFrame: TimeFrame,
  format: string,
  nonBreak?: boolean
) => {
  return timeFrameFormats(timeFrame, format, format, nonBreak);
};

export const sortTimeFrames = (
  timeFrames: TimeFrame[],
  granularity: ManipulateType
) => {
  return timeFrames.sort((a, b) => {
    if (a.start.isBefore(b.start, granularity)) return -1;
    else if (a.start.isAfter(b.start, granularity)) return 1;
    // if start times are the same, sort by longest time frame first
    if (a.end.isAfter(b.end, granularity)) return -1;
    else if (a.end.isBefore(b.end, granularity)) return 1;
    return 0;
  });
};

// Will give a string that represents the time frame, but will not
// print day, month, or year twice if they are the same.
// (Will also not print the year if it is the current year).
export const sparseTimeFrameFormat = (timeFrame: TimeFrame): string => {
  const now = dayjs();
  if (timeFrame.start.year() !== timeFrame.end.year()) {
    return timeFrameFormat(timeFrame, monthDayYearTimeF);
  }
  // don't show year if it is this year.
  if (now.year() === timeFrame.end.year()) {
    if (timeFrame.start.month() !== timeFrame.end.month()) {
      return timeFrameFormat(timeFrame, monthDayTimeF);
    }
    if (timeFrame.start.day() !== timeFrame.end.day()) {
      return timeFrameFormats(
        timeFrame,
        `${monthLongF}; ${dayTimeF}`,
        dayTimeF
      );
    }
    return timeFrameFormats(
      timeFrame,
      `${monthShortF} ${dayF}; ${timeF}`,
      timeF
    );
  }

  if (timeFrame.start.month() !== timeFrame.end.month()) {
    return timeFrameFormats(
      timeFrame,
      `${yearF}; ${monthDayTimeF}`,
      monthDayTimeF
    );
  }
  if (timeFrame.start.day() !== timeFrame.end.day()) {
    return timeFrameFormats(
      timeFrame,
      `${monthLongF} ${yearF}; ${dayTimeF}`,
      dayTimeF
    );
  }
  return timeFrameFormats(timeFrame, monthDayYearTimeF, timeF);
};

// TODO: fix this function to use the correct language
export const dateToLocalTime = (
  utcDateString: string,
  format: "date" | "dateWithDay" | "time" | "dateTime" | "all",
  intlLocale: Intl.LocalesArgument = "en-US"
): string => {
  if (utcDateString === "") return "";
  const date = new Date(utcDateString);
  let language: Intl.LocalesArgument = intlLocale;

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    weekday: format === "dateWithDay" || format === "all" ? "short" : undefined,
    hour:
      format === "time" || format === "dateTime" || format === "all"
        ? "2-digit"
        : undefined,
    minute:
      format === "time" || format === "dateTime" || format === "all"
        ? "2-digit"
        : undefined,
    second: format === "time" || format === "all" ? "2-digit" : undefined,
    timeZoneName: format === "all" ? "short" : undefined,
  };

  return date.toLocaleString(language, options);
};

/**
 * Format an event timestamp for read-only UI in UTC.
 * Example: `2026-08-05T08:36:13.595Z` → `Wed, 05 Aug 2026 08:37 GMT`
 * Seconds are rounded up to the next minute; hours and minutes only.
 */
export function formatUtcDateTime(
  value: string | null | undefined
): string {
  if (value == null || String(value).trim() === "") return "";
  const date = new Date(String(value).trim());
  if (Number.isNaN(date.getTime())) return "";

  let ms = date.getTime();
  const remainder = ms % 60_000;
  if (remainder > 0) {
    ms = ms - remainder + 60_000;
  }

  return new Date(ms)
    .toUTCString()
    .replace(/(\d{2}:\d{2}):\d{2} GMT$/, "$1 GMT");
}

/**
 * Parse a persisted lease/offer calendar date (ISO midnight UTC or legacy
 * YYYY/MM/DD) into a UTC Date at that calendar day.
 */
export function parseUTCDateOnly(iso: string): Date | null {
  const trimmed = iso.trim();
  if (!trimmed) return null;
  const match = /^(\d{4})[-/](\d{2})[-/](\d{2})/.exec(trimmed);
  if (match) {
    return new Date(
      Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    );
  }
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  );
}

/**
 * Format lease calendar dates for read-only UI.
 * Uses the UTC calendar day so midnight-UTC ISO values do not shift a day
 * earlier in western timezones (unlike `dateToLocalTime`).
 */
export function formatDisplayDateOnly(
  value: string | null | undefined,
  format: "date" | "dateWithDay" = "date",
  intlLocale: Intl.LocalesArgument = "en-US"
): string {
  if (value == null || String(value).trim() === "") return "";
  const date = parseUTCDateOnly(String(value));
  if (!date) return "";
  return date.toLocaleString(intlLocale, {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
    weekday: format === "dateWithDay" ? "short" : undefined,
  });
}

/**
 * Dayjs value for Ant Design DatePicker from a persisted lease calendar date.
 * Uses local calendar components matching the UTC date so the picker shows
 * the intended day (not the previous evening in US timezones).
 */
export function parseLeaseDateForPicker(
  value: string | null | undefined
): dayjs.Dayjs | null {
  if (value == null || String(value).trim() === "") return null;
  const date = parseUTCDateOnly(String(value));
  if (!date) return null;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return dayjs(`${y}-${m}-${d}`);
}

/**
 * Persist a DatePicker dayjs value as ISO-8601 midnight UTC.
 */
export function toPersistedLeaseDateIso(value: dayjs.Dayjs): string {
  return new Date(
    Date.UTC(value.year(), value.month(), value.date())
  ).toISOString();
}

/**
 * Generates a list of month-year pairs for rent computation between two dates.
 * For rent purposes, we include the start month and the end month if the end date
 * falls within that month.
 *
 * Month indexing: 0-based (0 = January, 11 = December), aligned with
 * `transaction.month`, `MonthlyStatusReport.month`, and finance URL params.
 *
 * @param startDate - ISO string of the start date
 * @param endDate - ISO string of the end date
 * @returns Array of objects containing month and year
 */
export const getRentMonths = (
  startDate: string,
  endDate: string
): Array<{ month: number; year: number }> => {
  if (startDate === "" || endDate === "") {
    return [];
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Validate dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    throw new Error("Invalid date format");
  }

  if (start > end) {
    throw new Error("Start date must be before end date");
  }

  // Return empty list if start and end dates are the same
  if (start.getTime() === end.getTime()) {
    return [];
  }

  const months: Array<{ month: number; year: number }> = [];

  // Use UTC methods to avoid timezone issues
  const startYear = start.getUTCFullYear();
  const startMonth = start.getUTCMonth();

  const endYear = end.getUTCFullYear();
  const endMonth = end.getUTCMonth();

  // Start from the first day of the start month
  let currentYear = startYear;
  let currentMonth = startMonth;

  // Include start month and end month
  while (
    currentYear < endYear ||
    (currentYear === endYear && currentMonth <= endMonth)
  ) {
    months.push({
      month: currentMonth,
      year: currentYear,
    });

    // Move to next month
    currentMonth++;
    if (currentMonth > 11) {
      currentMonth = 0;
      currentYear++;
    }
  }

  return months;
};
